import classNames from 'classnames';
import { AnimatePresence, m as motion, wrap } from 'framer-motion';
import { Dispatch, SetStateAction, forwardRef, useEffect, useState } from 'react';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Popup from '@/shared/components/shared/Popup';
import { tp } from '@/shared/lib/typograf';
import GalleryPopup from '../../popups/GalleryPopup';
import Image from '../../shared/Image';
import Pagination from '../../shared/Pagination';
import Typografed from '../../shared/Typografed';
import CloseButton from '../CloseButton';
import ConstructorButton from '../ConstructorButton';
import { StandBookingType, useMapStateContext } from '../InhouseMap/context';
import { AppPavilionData } from '../types';
import StandPopupInfo from './StandPopupInfo';

type Props = React.HTMLAttributes<HTMLElement> & {
    pavilionSlug: string;
    setPavilionSlug: Dispatch<SetStateAction<string>>;
    standsData: AppPavilionData[];
};

export const STAND_POPUP_NAME = 'constructor-stand-popup';

const StandPopup = forwardRef<HTMLDivElement, Props>(({ standsData, pavilionSlug, setPavilionSlug, ...props }, ref) => {
    const { bookingType, setBookingType } = useMapStateContext();
    let stands: AppPavilionData[];

    let activeData: any;

    switch (bookingType) {
        case 'unset':
            stands = standsData;
            break;
        case 'booked':
            stands = standsData.filter((stand) => !!stand.booking);
            break;
        case 'available':
            stands = standsData.filter((stand) => !stand.booking);
    }
    activeData = stands.find((stand) => stand.textSlug === pavilionSlug.toLowerCase());

    const activeStandIndex = stands.findIndex((stand, index) => {
        return stand.textSlug.toLowerCase().trim() === pavilionSlug.toLowerCase().trim();
    });

    const { closePopup } = useSetOpenedPopupsState();
    const [lastRealIndex, setLastRealIndex] = useState(activeStandIndex);
    const currentRealIndex = activeStandIndex;
    const logo = activeData?.booking?.company.logo || activeData?.booking?.company.previewLogo;

    const hasBusyStands = standsData.some((stand) => !!stand.booking);

    useEffect(() => {
        if (!activeData) {
            closePopup(STAND_POPUP_NAME);
        }
    }, [activeData, closePopup]);

    return (
        <Popup
            {...props}
            name={STAND_POPUP_NAME}
            ref={ref}
            overlay
            className={classNames('stand-popup js-stand-popup', props.className)}
        >
            <div className="stand-popup-content">
                <div className="stand-popup-content-inner">
                    <div className="stand-popup-top">
                        <div className="text-l stand-popup-top__title">
                            <Typografed>О компании</Typografed>
                        </div>

                        <CloseButton
                            onClick={() => {
                                closePopup(STAND_POPUP_NAME);
                                setPavilionSlug('');
                            }}
                        />
                    </div>

                    <AnimatePresence mode="wait">
                        {activeData && (
                            <motion.div
                                key={activeData.textSlug}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                {hasBusyStands && (
                                    <ul className="list-unstyled stand-popup-booking-states__list">
                                        {(
                                            [
                                                {
                                                    text: 'Все стенды',
                                                    bookingType: 'unset',
                                                },
                                                {
                                                    text: 'Свободные',
                                                    bookingType: 'available',
                                                },
                                                {
                                                    text: 'Занятые',
                                                    bookingType: 'booked',
                                                },
                                            ] satisfies { text: string; bookingType: StandBookingType }[]
                                        ).map((data, i) => (
                                            <li key={i} className="stand-popup-booking-states__list-item">
                                                <ConstructorButton
                                                    tag="span"
                                                    geometryVariant="wide"
                                                    variant={bookingType === data.bookingType ? 'dark-blue' : 'outline'}
                                                    className="stand-popup-booking-state-btn"
                                                    onClick={() => setBookingType(data.bookingType)}
                                                >
                                                    {data.text}
                                                </ConstructorButton>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {activeData?.booking ? (
                                    <div className="stand-popup-booking">
                                        {logo?.src && (
                                            <div className="stand-popup-booking__logo">
                                                <Image
                                                    src={logo.src}
                                                    alt={logo.alt || activeData.booking.company.name}
                                                    width={logo.width}
                                                    height={logo.height}
                                                    className="img-fluid stand-popup-booking__logo-img"
                                                />
                                            </div>
                                        )}
                                        <div className="stand-popup-booking__info">
                                            <div className="text-l stand-popup-booking__info-name">
                                                <Typografed>{activeData.booking.company.name}</Typografed>
                                            </div>
                                            {activeData.slug && (
                                                <div className="text-s stand-popup-booking__info-stand">
                                                    Стенд {activeData.slug.toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="stand-popup-booking__description">
                                            {activeData.booking.company.description && (
                                                <div
                                                    className="text-m stand-popup-booking__description-text"
                                                    dangerouslySetInnerHTML={{
                                                        __html: tp(activeData.booking.company.description),
                                                    }}
                                                ></div>
                                            )}
                                            {activeData.booking.company.url && (
                                                <div className="stand-popup-booking__description-link">
                                                    <a
                                                        href={activeData.booking.company.url}
                                                        className="text-s link link--underlined"
                                                        target="_blank"
                                                        rel="noreferrer nofollow"
                                                    >
                                                        {activeData.booking.company.url.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <StandPopupInfo activeData={activeData} />
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {stands && (
                        <div className="stand-popup-pagination-wrapper">
                            <Pagination
                                numSlides={stands.length}
                                currentRealIndex={currentRealIndex}
                                lastRealIndex={lastRealIndex}
                                prevElClass="reviews-slider-prev-btn"
                                nextElClass="reviews-slider-next-btn"
                                wrapperClass="reviews-pagination-wrapper stand-popup-pagination"
                                onPrevClick={() => {
                                    setLastRealIndex(currentRealIndex);
                                    const prevPavilion = stands[wrap(0, stands.length, currentRealIndex - 1)];
                                    setPavilionSlug(prevPavilion?.textSlug || '');
                                }}
                                onNextClick={() => {
                                    setLastRealIndex(currentRealIndex);
                                    const nextPavilion = stands[wrap(0, stands.length, currentRealIndex + 1)];
                                    setPavilionSlug(nextPavilion?.textSlug || '');
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {activeData && activeData.gallery.length > 0 && (
                <GalleryPopup
                    title="Как это было"
                    slides={activeData.gallery.map((item: any) => ({
                        img: item.img,
                        title: activeData.name,
                        price: activeData.price,
                        square: activeData.space,
                    }))}
                />
            )}
        </Popup>
    );
});

StandPopup.displayName = 'StandPopup';

export default StandPopup;
